/**
 * SFX Pool — preload-and-play via cached AudioBuffer + per-play source nodes.
 *
 * Web Audio API (W3C): AudioBufferSourceNode.start() can be called exactly once per
 * node instance — calling it again throws InvalidStateError. To replay a sound, create
 * a fresh AudioBufferSourceNode from the same cached AudioBuffer each time.
 * @see https://webaudio.github.io/web-audio-api/#AudioBufferSourceNode
 */

import type { SfxId } from './types';
import type { AudioAssetLoader } from '../assets/audio-loader';

// ── Public interfaces ─────────────────────────────────────────────────────────

export interface SfxPool {
  /**
   * Fetch + decode each id. Parallel loads via Promise.allSettled; per-id
   * failures substitute a silent buffer and do NOT reject the overall promise.
   */
  preload(ids: readonly SfxId[]): Promise<void>;

  /**
   * Look up the cached AudioBuffer for id, construct a fresh
   * AudioBufferSourceNode (single-use per Web Audio spec), connect to
   * opts.destination ?? sfxGain, and call start(0).
   * No-ops with a warning if id was not preloaded.
   */
  play(id: SfxId, opts?: { destination?: AudioNode }): void;

  /**
   * Clear the buffer map. Idempotent. Does NOT close the AudioContext —
   * the AudioManager owns context lifecycle.
   */
  dispose(): void;
}

export interface SfxPoolDeps {
  readonly audioContext: AudioContext;
  readonly loader: AudioAssetLoader;
  readonly resolveUrl: (id: SfxId) => string;
  readonly sfxGain: GainNode;
}

// ── Factory ───────────────────────────────────────────────────────────────────

export function createSfxPool(deps: SfxPoolDeps): SfxPool {
  const { audioContext, loader, resolveUrl, sfxGain } = deps;
  const buffers = new Map<SfxId, AudioBuffer>();

  async function preload(ids: readonly SfxId[]): Promise<void> {
    await Promise.allSettled(
      ids.map(async (id) => {
        try {
          const buf = await loader.load(resolveUrl(id));
          buffers.set(id, buf);
        } catch (err) {
          console.warn('[sfx-pool] load failed for', id, ':', err, '— falling back to silence');
          buffers.set(id, loader.createSilentBuffer(0.1));
        }
      }),
    );
  }

  function play(id: SfxId, opts?: { destination?: AudioNode }): void {
    const buf = buffers.get(id);
    if (buf === undefined) {
      console.warn('[sfx-pool] play called for unpreloaded id:', id, '— no-op');
      return;
    }

    // Per Web Audio spec: each AudioBufferSourceNode is single-use.
    // We cache the buffer, not the node. A fresh node is created per play().
    const source = audioContext.createBufferSource();
    source.buffer = buf;
    source.connect(opts?.destination ?? sfxGain);
    source.start(0);
  }

  function dispose(): void {
    buffers.clear();
  }

  return { preload, play, dispose };
}
